import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addEquipment, clearError } from '../redux/slices/equipmentsSlice';
import { toast } from 'react-toastify';
import { LoadingModal } from '../components/LoadingModal';
import { LocationAutocomplete } from './LocationAutocomplete';

const AddEquipmentModal = ({ setAddEquipmentModal }) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.equipments.loading);
  const error = useSelector((state) => state.equipments.error);

  const [name, setName] = useState('');
  const [qty, setQty] = useState('');
  const [rentalPrice, setRentalPrice] = useState('');
  const [location, setLocation] = useState('');
  const [type, setType] = useState('');
  const [images, setImages] = useState([]);
  const [imageInput, setImageInput] = useState('');
  const [specs, setSpecs] = useState('');
  const [description, setDescription] = useState('');
  const [tutorials, setTutorials] = useState('');
  const [selfTransport, setSelfTransport] = useState('');

  useEffect(() => {
    // Clear any existing errors when component mounts
    dispatch(clearError());

    // Clean up when component unmounts
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleDismissError = () => {
    dispatch(clearError());
  };

  const handleAddImage = () => {
    if (imageInput.trim()) {
      setImages((prevImages) => [...prevImages, imageInput.trim()]);
      setImageInput('');
    }
  };

  const handleRemoveImage = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const parsedQty = parseInt(qty, 10);
    const parsedRentalPrice = parseFloat(rentalPrice);

    if (isNaN(parsedQty) || isNaN(parsedRentalPrice)) {
      toast.error('Please enter valid numbers for quantity and rental price.');
      return;
    }

    try {
      await dispatch(
        addEquipment({
          name,
          qty: parsedQty,
          rentalPrice: parsedRentalPrice,
          location,
          type,
          images,
          specs,
          description,
          tutorials,
          selfTransport,
        }),
      ).unwrap();

      setName('');
      setQty('');
      setRentalPrice('');
      setLocation('');
      setType('');
      setImages([]);
      setSpecs('');
      setDescription('');
      setTutorials('');
      setSelfTransport('');
      toast.success('Equipment added successfully!');
      setAddEquipmentModal(false);
    } catch (error) {
      console.error('Failed to add equipment:', error);
    }
  };

  const canSubmit =
    name &&
    qty &&
    rentalPrice &&
    location &&
    type &&
    specs &&
    description &&
    selfTransport &&
    tutorials;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      {(loading || error) && (
        <LoadingModal
          message="Adding equipment..."
          error={error}
          onClose={handleDismissError}
        />
      )}
      <div className="bg-white p-6 rounded-lg w-1/3 h-3/4 overflow-hidden flex flex-col">
        <h3 className="text-lg font-bold mb-4">Add New Equipment</h3>
        <div className="overflow-y-auto flex-grow pr-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block font-semibold" htmlFor="name">
                Name
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Quantity */}
            <div>
              <label className="block font-semibold" htmlFor="qty">
                Quantity
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                id="qty"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Rental Price */}
            <div>
              <label className="block font-semibold" htmlFor="rentalPrice">
                Rental Price
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                id="rentalPrice"
                value={rentalPrice}
                onChange={(e) => setRentalPrice(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Location */}
            <div>
              <LocationAutocomplete
                id="location"
                name="location"
                label="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                required={true}
              />
            </div>

            {/* Type */}
            <div>
              <label className="block font-semibold" htmlFor="type">
                Type
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>

            {/* Images */}
            <div>
              <label className="block font-semibold" htmlFor="images">
                Image URLs
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  id="imageInput"
                  value={imageInput}
                  onChange={(e) => setImageInput(e.target.value)}
                  className="flex-grow p-2 border border-gray-300 rounded-md"
                  placeholder="Enter image URL"
                />
                <button
                  type="button"
                  onClick={handleAddImage}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold px-4 rounded-md"
                >
                  Add
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {images.map((image, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <img
                      src={image}
                      alt={`Preview ${index}`}
                      className="w-16 h-16 object-cover rounded-md"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="text-red-500 text-xs mt-1"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block font-semibold" htmlFor="tutorials">
                Self-Transport Requirements
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="selfTransport"
                value={selfTransport}
                onChange={(e) => setSelfTransport(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="4"
                placeholder="If user selects they do not need pickup or delivery assistance. E.g. Requires heavy-duty trailer, 2-ton towing capacity, flatbed with ramps, tie-down straps, etc."
                required
              />
            </div>

            {/* Specifications */}
            <div>
              <label className="block font-semibold" htmlFor="specs">
                Specifications
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="specs"
                value={specs}
                onChange={(e) => setSpecs(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="2"
                placeholder="E.g. 6 ft working width, 540 RPM PTO, requires 30+ HP tractor, adjustable tilling depth up to 6 inches."
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block font-semibold" htmlFor="description">
                Description
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="E.g. Heavy-duty rotary tiller ideal for breaking up compact soil and preparing seedbeds. Easy to attach to most tractors."
                required
              />
            </div>

            {/* Tutorials */}
            <div>
              <label className="block font-semibold" htmlFor="tutorials">
                Tutorials
                <span className="text-red-500 ml-1">*</span>
              </label>
              <textarea
                id="tutorials"
                value={tutorials}
                onChange={(e) => setTutorials(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="E.g. Connect PTO before lowering tiller. Keep bystanders at least 10 ft away. Watch video on attaching tiller safely. [Link to video]"
                required
              />
            </div>
          </form>
        </div>
        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={() => setAddEquipmentModal(false)}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-md"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !canSubmit}
          >
            {loading ? 'Adding...' : 'Add Equipment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export { AddEquipmentModal };
